import { useAuth } from '../contexts/AuthContext';
import type { UserMode } from '../types';

/**
 * Hook to detect demo mode state based on user authentication and subscription
 *
 * Three possible states:
 * - anonymous: User is not authenticated (is_anonymous = true)
 * - onboarding: User is authenticated but only has assessment access (subscription_plan = 'assessment_only')
 * - subscribed: User has full access (trial, paid, or other subscription plans)
 *
 * Demo mode (showing mock data) is active for anonymous and onboarding states
 */

interface UseDemoModeReturn {
  mode: UserMode;
  isDemoMode: boolean;
  isAnonymous: boolean;
  isOnboarding: boolean;
  isSubscribed: boolean;
}

export const useDemoMode = (): UseDemoModeReturn => {
  const { user } = useAuth();

  // Determine the current mode
  const getMode = (): UserMode => {
    if (!user) {
      return 'anonymous';
    }

    if (user.is_anonymous === true) {
      return 'anonymous';
    }

    // Check both user.subscription_plan and user.client?.subscription_plan for compatibility
    const subscriptionPlan = user.subscription_plan || user.client?.subscription_plan;

    if (subscriptionPlan === 'assessment_only') {
      return 'onboarding';
    }

    return 'subscribed';
  };

  const mode = getMode();
  const isDemoMode = mode === 'anonymous' || mode === 'onboarding';

  return {
    mode,
    isDemoMode,
    isAnonymous: mode === 'anonymous',
    isOnboarding: mode === 'onboarding',
    isSubscribed: mode === 'subscribed'
  };
};

export default useDemoMode;
